import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bank, Wallet, Money, CreditCard, Plus, CaretRight, PencilSimple } from "@phosphor-icons/react";
import { Drawer, DrawerContent, DrawerTitle } from "../components/ui/drawer";
import { toast } from "sonner";
import api from "../lib/api";
import { inr, ACCOUNT_TYPE_LABELS, curSym } from "../lib/format";

const ICONS = { bank: Bank, cash: Money, upi_wallet: Wallet, credit_card: CreditCard };
const blank = { name: "", account_type: "bank", owner_member_id: "", institution_name: "", opening_balance: "", credit_limit: "", statement_day: "", payment_due_day: "" };

export default function Accounts() {
  const [accounts, setAccounts] = useState(null);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/accounts").then((r) => setAccounts(r.data));
  useEffect(() => {
    load();
    api.get("/lookups").then((r) => setMembers(r.data.members)).catch(() => {});
  }, []);

  const memberName = (id) => members.find((m) => m.id === id)?.display_name;

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...form,
      owner_member_id: form.owner_member_id || null,
      opening_balance: parseFloat(form.opening_balance || 0),
      credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : null,
      statement_day: form.statement_day ? parseInt(form.statement_day) : null,
      payment_due_day: form.payment_due_day ? parseInt(form.payment_due_day) : null,
    };
    try {
      if (editId) await api.put(`/accounts/${editId}`, payload);
      else await api.post("/accounts", payload);
      toast.success(editId ? "Account updated" : "Account added");
      setOpen(false);
      setForm(blank);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const startEdit = (a) => {
    setForm({
      name: a.name,
      account_type: a.account_type,
      owner_member_id: a.owner_member_id || "",
      institution_name: a.institution_name || "",
      opening_balance: a.opening_balance != null ? String(a.opening_balance) : "",
      credit_limit: a.credit_limit != null ? String(a.credit_limit) : "",
      statement_day: a.statement_day != null ? String(a.statement_day) : "",
      payment_due_day: a.payment_due_day != null ? String(a.payment_due_day) : "",
    });
    setEditId(a.id);
    setOpen(true);
  };

  const input = "w-full h-12 px-4 rounded-xl bg-background border border-border";
  const grouped = (accounts || []).reduce((acc, a) => { (acc[a.account_type] ||= []).push(a); return acc; }, {});

  return (
    <div className="px-5 pt-8" data-testid="accounts-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold tracking-tight">Accounts</h1>
        <button data-testid="account-add-btn" onClick={() => { setForm(blank); setEditId(null); setOpen(true); }} className="h-11 px-4 rounded-xl bg-primary text-primary-foreground flex items-center gap-1 text-sm font-medium"><Plus size={16} /> Add</button>
      </div>

      {!accounts ? (
        <div className="py-10 flex justify-center"><div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
      ) : accounts.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground text-center">No accounts yet. Add your first one.</p>
      ) : (
        Object.entries(grouped).map(([type, list]) => (
          <div key={type} className="mt-5">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">{ACCOUNT_TYPE_LABELS[type]}</p>
            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              {list.map((a) => {
                const Icon = ICONS[a.account_type];
                return (
                  <Link key={a.id} to={`/more/accounts/${a.id}`} data-testid={`account-row-${a.id}`} className="flex items-center gap-3 px-4 py-3.5 border-b border-border last:border-0 active:bg-secondary/50">
                    <Icon size={20} className="text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.name}</p>
                      {(memberName(a.owner_member_id) || a.institution_name) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {[memberName(a.owner_member_id), a.institution_name].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {a.account_type === "credit_card" ? (
                        <p className="text-sm font-semibold tnum text-expense">{inr(a.outstanding)} due</p>
                      ) : (
                        <p className="text-sm font-semibold tnum">{inr(a.current_balance)}</p>
                      )}
                    </div>
                    <button
                      data-testid={`account-edit-${a.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(a); }}
                      className="h-10 w-10 flex items-center justify-center rounded-xl text-muted-foreground active:bg-secondary"
                      aria-label="Edit account"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <CaretRight size={14} className="text-muted-foreground" />
                  </Link>
                );
              })}
            </div>
          </div>
        ))
      )}

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-w-md mx-auto rounded-t-3xl bg-white">
          <DrawerTitle className="px-5 pt-2 pb-3 text-base font-heading font-semibold">{editId ? "Edit account" : "New account"}</DrawerTitle>
          <div className="px-5 pb-8 space-y-3 overflow-y-auto max-h-[70vh]">
            <input data-testid="account-form-name" className={input} placeholder="Account name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, v]) => (
                <button key={k} data-testid={`account-form-type-${k}`} onClick={() => setForm({ ...form, account_type: k })} className={`h-11 rounded-xl text-sm border ${form.account_type === k ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`}>{v}</button>
              ))}
            </div>
            <select data-testid="account-form-owner" className={input} value={form.owner_member_id} onChange={(e) => setForm({ ...form, owner_member_id: e.target.value })}>
              <option value="">Account holder (me)</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
            <input data-testid="account-form-institution" className={input} placeholder="Bank / institution (optional)" value={form.institution_name} onChange={(e) => setForm({ ...form, institution_name: e.target.value })} />
            <input data-testid="account-form-opening" type="number" inputMode="decimal" className={input} placeholder={form.account_type === "credit_card" ? "Opening balance (negative if owed)" : `Opening balance (${curSym()})`} value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
            {form.account_type === "credit_card" && (
              <>
                <input data-testid="account-form-limit" type="number" inputMode="decimal" className={input} placeholder={`Credit limit (${curSym()})`} value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="account-form-statement-day" type="number" min="1" max="31" className={input} placeholder="Statement day" value={form.statement_day} onChange={(e) => setForm({ ...form, statement_day: e.target.value })} />
                  <input data-testid="account-form-due-day" type="number" min="1" max="31" className={input} placeholder="Due day" value={form.payment_due_day} onChange={(e) => setForm({ ...form, payment_due_day: e.target.value })} />
                </div>
              </>
            )}
            <button data-testid="account-form-save" onClick={save} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-medium">Save account</button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
